/* RESPONSIBLE TEAM: team-knowledge-foundations */

import Service from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import type ContentFragment from 'embercom/models/content-service/content-fragment';
import type KnowledgeHubApi from 'embercom/lib/knowledge-hub/list-api';
import { type KnowledgeHubApiQueryParams } from 'embercom/lib/knowledge-hub/list-api';
import { type SortDirection } from 'embercom/lib/knowledge-hub/constants';

export default class UnifiedContentStateService extends Service {
  @tracked searchTerm?: string;
  @tracked linkedUrls?: string[];
  @tracked titleTerm?: string;
  @tracked bodyTerm?: string;
  @tracked urlTerm?: string;
  @tracked locale = '';
  @tracked predicates?: any[];
  @tracked status = '';
  @tracked syncStatus?: string[] | null;
  @tracked chatbotState?: string;
  @tracked copilotState?: string;
  @tracked salesAgentState?: string;
  @tracked activeFilter = '';
  @tracked filtersApplied: string[] = [];
  @tracked aiContentSegmentIds?: any[];
  @tracked selectedTypes: any[] = [];
  @tracked createdBy?: string;
  @tracked lastUpdatedBy?: string;
  @tracked tagIds?: any[];
  @tracked tagOperator: 'IN' | 'NIN' = 'IN';
  @tracked collectionIds?: any[];
  @tracked writtenByIds?: any[];
  @tracked urlSourceId = '';
  @tracked selectedHelpCenterIds?: any[];
  @tracked selectedFragments = new Set<ContentFragment>();
  @tracked unselectedFragments = new Set<ContentFragment>();
  @tracked isBulkSelection = false;
  @tracked sortBy?: string;
  @tracked sortDirection?: SortDirection;

  listApi?: KnowledgeHubApi;

  @action
  setSearchTerm(searchTerm?: string) {
    this.searchTerm = searchTerm;
  }

  @action
  setLinkedUrls(linkedUrls?: string[]) {
    this.linkedUrls = linkedUrls;
  }

  @action
  setTitleTerm(titleTerm?: string) {
    this.titleTerm = titleTerm;
  }

  @action
  setBodyTerm(bodyTerm?: string) {
    this.bodyTerm = bodyTerm;
  }

  @action
  setUrlTerm(urlTerm?: string) {
    this.urlTerm = urlTerm;
  }

  @action
  setLocale(locale: string) {
    this.locale = locale;
  }

  @action
  setPredicates(predicates: any[]) {
    this.predicates = predicates;
  }

  @action
  setStatus(status: string) {
    this.status = status;
  }

  @action
  setSyncStatus(syncStatus: string[] | null) {
    this.syncStatus = syncStatus;
  }

  @action
  setChatbotState(chatbotState: string) {
    this.chatbotState = chatbotState;
  }

  @action
  setCopilotState(copilotState: string) {
    this.copilotState = copilotState;
  }

  @action
  setSalesAgentState(salesAgentState: string) {
    this.salesAgentState = salesAgentState;
  }

  @action
  setActiveFilter(activeFilter: string) {
    this.activeFilter = activeFilter;
  }

  @action
  setFiltersApplied(filtersApplied: string[]) {
    this.filtersApplied = filtersApplied;
  }

  @action
  setAiContentSegmentIds(aiContentSegmentIds: any[]) {
    this.aiContentSegmentIds = aiContentSegmentIds;
  }

  @action
  setSelectedTypes(selectedTypes: any[]) {
    this.selectedTypes = selectedTypes;
  }

  @action
  setCreatedBy(createdBy: string) {
    this.createdBy = createdBy;
  }

  @action
  setLastUpdatedBy(lastUpdatedBy: string) {
    this.lastUpdatedBy = lastUpdatedBy;
  }

  @action
  setTagIds(tagIds: any[]) {
    this.tagIds = tagIds;
  }

  @action
  setTagOperator(tagOperator: 'IN' | 'NIN') {
    this.tagOperator = tagOperator;
  }

  @action
  setCollectionIds(collectionIds: any[]) {
    this.collectionIds = collectionIds;
  }

  @action
  setWrittenByIds(writtenByIds: any[]) {
    this.writtenByIds = writtenByIds;
  }

  @action
  setUrlSourceId(urlSourceId?: string) {
    this.urlSourceId = urlSourceId || '';
  }

  @action
  setSelectedHelpCenterIds(selectedHelpCenterIds?: string[]) {
    this.selectedHelpCenterIds = selectedHelpCenterIds || [];
  }

  @action
  setSelectedFragments(fragments: Set<ContentFragment>) {
    this.selectedFragments = fragments;
  }

  @action
  setUnselectedFragments(fragments: Set<ContentFragment>) {
    this.unselectedFragments = fragments;
  }

  @action
  setIsBulkSelection(isBulkSelection: boolean) {
    this.isBulkSelection = isBulkSelection;
  }

  @action
  setSortBy(sortBy?: string) {
    this.sortBy = sortBy;
  }

  @action
  setSortDirection(sortDirection?: SortDirection) {
    this.sortDirection = sortDirection;
  }

  @action
  resetSelectedFragments() {
    this.selectedFragments = new Set<ContentFragment>();
    this.unselectedFragments = new Set<ContentFragment>();
    this.isBulkSelection = false;
  }

  @action
  resetFilters() {
    this.searchTerm = undefined;
    this.linkedUrls = undefined;
    this.titleTerm = undefined;
    this.bodyTerm = undefined;
    this.urlTerm = undefined;
    this.locale = '';
    this.predicates = undefined;
    this.status = '';
    this.syncStatus = undefined;
    this.chatbotState = undefined;
    this.copilotState = undefined;
    this.salesAgentState = undefined;
    this.activeFilter = '';
    this.filtersApplied = [];
    this.aiContentSegmentIds = undefined;
    this.selectedTypes = [];
    this.createdBy = undefined;
    this.lastUpdatedBy = undefined;
    this.tagIds = undefined;
    this.tagOperator = 'IN';
    this.collectionIds = undefined;
    this.writtenByIds = undefined;
    this.urlSourceId = '';
    this.selectedHelpCenterIds = undefined;
    this.sortBy = undefined;
    this.sortDirection = undefined;
  }

  get hasAppliedFilter(): boolean {
    return !!(
      this.filtersApplied.length > 0 ||
      this.searchTerm ||
      (this.aiContentSegmentIds && this.aiContentSegmentIds.length > 0) ||
      (this.selectedTypes && this.selectedTypes.length > 0) ||
      this.createdBy ||
      this.lastUpdatedBy
    );
  }

  get hasActiveSearch(): boolean {
    return !!(
      this.searchTerm ||
      this.titleTerm ||
      this.bodyTerm ||
      this.urlTerm ||
      this.linkedUrls
    );
  }

  /**
   * Converts the current filter state into the KnowledgeHubApiQueryParams shape
   * expected by KnowledgeHubApi.searchParamsFromQueryParams().
   *
   * When adding a new tracked filter property to this service, add the corresponding
   * mapping here so it is preserved across refreshModel transitions on content routes.
   */
  toQueryParams(): KnowledgeHubApiQueryParams {
    let hasTagIds = this.tagIds && this.tagIds.length > 0;

    return {
      searchTerm: this.searchTerm || undefined,
      titleTerm: this.titleTerm || undefined,
      bodyTerm: this.bodyTerm || undefined,
      urlTerm: this.urlTerm || undefined,
      linkedUrls: this.linkedUrls?.join(',') || undefined,
      locale: this.locale || undefined,
      status: this.status || undefined,
      createdBy: this.createdBy || undefined,
      lastUpdatedBy: this.lastUpdatedBy || undefined,
      predicates: this.predicates?.length ? JSON.stringify(this.predicates) : undefined,
      selectedTypes: this.selectedTypes?.length ? this.selectedTypes.join(',') : undefined,
      selectedHelpCenterIds: this.selectedHelpCenterIds?.length
        ? this.selectedHelpCenterIds.join(',')
        : undefined,
      chatbotState: this.chatbotState || undefined,
      copilotState: this.copilotState || undefined,
      salesAgentState: this.salesAgentState || undefined,
      tagIds: hasTagIds ? this.tagIds!.join(',') : undefined,
      tagOperator: hasTagIds ? this.tagOperator : undefined,
      collectionIds: this.collectionIds?.length ? this.collectionIds.join(',') : undefined,
      writtenByIds: this.writtenByIds?.length ? this.writtenByIds.join(',') : undefined,
      aiContentSegmentIds: this.aiContentSegmentIds?.length
        ? this.aiContentSegmentIds.join(',')
        : undefined,
      sortByParam: this.sortBy || undefined,
      sortDirectionParam: this.sortDirection || undefined,
    } as KnowledgeHubApiQueryParams;
  }

  registerListApi(listApi: KnowledgeHubApi) {
    this.listApi = listApi;
  }

  refreshContent() {
    this.listApi?.refreshPageAfterDelay();
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'unified-content-state': typeof UnifiedContentStateService;
  }
}
